import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStatuses } from "@/context/StatusContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Save, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import UserAvatar from "@/components/UserAvatar";
import { AVATAR_PRESETS } from "@/lib/avatars";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { statuses } = useStatuses();
  const [name, setName] = useState(user?.name || "");
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [prefs, setPrefs] = useState({});
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { setName(user?.name || ""); setAvatar(user?.avatar || ""); }, [user]);
  useEffect(() => { api.get("/notifications/prefs").then((r) => setPrefs(r.data.prefs)).catch(() => {}); }, []);
  useEffect(() => { api.get("/notifications").then((r) => setNotifs(r.data.notifications || [])).catch(() => {}); }, []);

  const saveProfile = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", { name: name.trim(), avatar });
      await refreshUser();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    } finally { setSavingProfile(false); }
  };

  const setPref = (key, field, val) =>
    setPrefs((p) => ({ ...p, [key]: { ...(p[key] || { in_app: false, email: false }), [field]: val } }));

  const savePrefs = async () => {
    setSavingPrefs(true);
    try { await api.put("/notifications/prefs", { prefs }); toast.success("Notification preferences saved"); }
    catch { toast.error("Failed to save preferences"); }
    finally { setSavingPrefs(false); }
  };

  const rows = [
    ...statuses.map((s) => ({ key: s.key, label: `Status changed to “${s.label}”` })),
    { key: "overdue", label: "A shipment becomes overdue" },
  ];

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-[#111411] tracking-tight">My profile</h1>
        <p className="text-muted-foreground mt-1">Update your name, pick an avatar, and choose which events notify you.</p>
      </div>

      <div className="rounded-lg border border-border bg-white p-6 space-y-4" data-testid="profile-notifications">
        <div className="flex items-center gap-2 text-[#0B3D3D]">
          <Bell className="h-5 w-5" />
          <h3 className="font-heading font-bold text-[#111411]">Recent notifications</h3>
        </div>
        {notifs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notifications yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {notifs.slice(0, 10).map((n) => (
              <button key={n.id} data-testid={`profile-notif-${n.id}`}
                      onClick={() => n.shipment_id && navigate(`/shipments/${n.shipment_id}`)}
                      className={`w-full text-left py-2.5 flex items-start gap-2 hover:bg-[#F9F9F8] transition-colors ${n.read ? "" : "font-medium"}`}>
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#C5A253] shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm text-[#111411]">{n.title}</p>
                  <p className="text-xs text-muted-foreground">{n.body}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-white p-6 space-y-5">
        <div className="flex items-center gap-4">
          <UserAvatar name={name} avatar={avatar} size={64} />
          <div className="flex-1">
            <Label>Display name</Label>
            <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 max-w-sm" />
          </div>
        </div>
        <div>
          <Label className="mb-2 block">Choose an avatar</Label>
          <div className="flex flex-wrap gap-3">
            <button type="button" data-testid="avatar-monogram" onClick={() => setAvatar("")}
                    className={`rounded-full transition-transform hover:scale-105 ${avatar === "" ? "ring-2 ring-[#0B3D3D] ring-offset-2" : ""}`}>
              <UserAvatar name={name} avatar="" size={52} />
            </button>
            {AVATAR_PRESETS.map((a) => (
              <button key={a} type="button" data-testid={`avatar-${a}`} onClick={() => setAvatar(a)}
                      className={`rounded-full transition-transform hover:scale-105 ${avatar === a ? "ring-2 ring-[#0B3D3D] ring-offset-2" : ""}`}>
                <img src={`/avatars/${a}.jpg`} alt={a} className="h-[52px] w-[52px] rounded-full object-cover border border-black/5" />
              </button>
            ))}
          </div>
        </div>
        <Button onClick={saveProfile} disabled={savingProfile} data-testid="profile-save" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
          <Save className="h-4 w-4" /> Save profile
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-white p-6 space-y-4">
        <div className="flex items-center gap-2 text-[#0B3D3D]">
          <Bell className="h-5 w-5" />
          <h3 className="font-heading font-bold text-[#111411]">Notifications</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Choose the events you want to hear about and how. In-app shows in your bell; email is sent to {user?.email}.
        </p>
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_4rem_4rem] gap-4 px-4 py-2 bg-[#F4F6F4] text-xs font-medium text-muted-foreground">
            <span>Event</span><span className="text-center">In-app</span><span className="text-center">Email</span>
          </div>
          {rows.map((r) => {
            const p = prefs[r.key] || { in_app: false, email: false };
            return (
              <div key={r.key} data-testid={`pref-row-${r.key}`} className="grid grid-cols-[1fr_4rem_4rem] gap-4 px-4 py-2.5 border-t border-border items-center">
                <span className="text-sm text-[#111411]">{r.label}</span>
                <span className="flex justify-center"><Checkbox checked={!!p.in_app} onCheckedChange={(v) => setPref(r.key, "in_app", !!v)} data-testid={`pref-${r.key}-inapp`} /></span>
                <span className="flex justify-center"><Checkbox checked={!!p.email} onCheckedChange={(v) => setPref(r.key, "email", !!v)} data-testid={`pref-${r.key}-email`} /></span>
              </div>
            );
          })}
        </div>
        <Button onClick={savePrefs} disabled={savingPrefs} data-testid="prefs-save" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
          <Save className="h-4 w-4" /> Save notification preferences
        </Button>
      </div>
    </div>
  );
}
